import _extends from "@babel/runtime/helpers/extends";
import React, { useRef, forwardRef, useImperativeHandle, useState, useMemo, useCallback, useEffect } from 'react';
import { ContextLayout } from '../context-provider';
import MultiPicker from '../picker-view/components/multi-picker';
import PickerCell from '../picker-view/components/picker-cell';
import Cascader from '../picker-view/components/cascader';
import { useMountedState } from '../_helpers';
export * from './type';
export { MultiPicker, PickerCell, Cascader };

/**
 * 选择器视图组件，不含弹窗，方便使用方灵活定制选择器。
 * @en The picker view component, not has contain popup, which is convenient for the user to flexibly customize the picker.
 * @type 数据输入
 * @type_en Data Entry
 * @name 选择器视图
 * @name_en PickerView
 */
var PickerView = /*#__PURE__*/forwardRef(function (props, ref) {
  var _props$className = props.className,
      className = _props$className === void 0 ? '' : _props$className,
      itemStyle = props.itemStyle,
      _props$cascade = props.cascade,
      cascade = _props$cascade === void 0 ? true : _props$cascade,
      _props$cols = props.cols,
      cols = _props$cols === void 0 ? 3 : _props$cols,
      _props$rows = props.rows,
      rows = _props$rows === void 0 ? 5 : _props$rows,
      data = props.data,
      _props$disabled = props.disabled,
      disabled = _props$disabled === void 0 ? false : _props$disabled,
      _props$clickable = props.clickable,
      clickable = _props$clickable === void 0 ? true : _props$clickable,
      value = props.value,
      _props$hideEmptyCols = props.hideEmptyCols,
      hideEmptyCols = _props$hideEmptyCols === void 0 ? false : _props$hideEmptyCols,
      onPickerChange = props.onPickerChange,
      _props$touchToStop = props.touchToStop,
      touchToStop = _props$touchToStop === void 0 ? false : _props$touchToStop;

  var _useState = useState(0),
      itemHeight = _useState[0],
      setItemHeight = _useState[1];

  var _useState2 = useState(0),
      wrapperHeight = _useState2[0],
      setWrapperHeight = _useState2[1];

  var _useMountedState = useMountedState(value),
      scrollValue = _useMountedState[0],
      setScrollValue = _useMountedState[1];

  var wrapperRef = useRef(null);
  var domRef = useRef(null);
  var barRef = useRef(null);
  var scrollValueRef = useRef(value);
  var pickerCellsRef = useRef([]);
  var cascaderRef = useRef(null);
  var innerData = useMemo(function () {
    var newData;

    var isArray = function isArray(dt) {
      return dt ? Array.isArray(dt[0]) : false;
    };

    var isStrOrNum = function isStrOrNum(dt) {
      return typeof dt[0][0] === 'string' || typeof dt[0][0] === 'number';
    };

    if (isArray(data)) {
      if (isStrOrNum(data)) {
        newData = data.map(function (item) {
          return item.map(function (cell) {
            return {
              label: cell,
              value: cell
            };
          });
        });
      } else {
        newData = data;
      }
    } else {
      newData = [data];
    }

    return newData;
  }, [data]);
  useEffect(function () {
    scrollValueRef.current = scrollValue;
  }, [scrollValue]);

  var getAllColumnValues = function getAllColumnValues() {
    return scrollValueRef.current;
  };

  function getColumnValue(index) {
    if (index === void 0) {
      index = 0;
    }

    return scrollValueRef.current[index];
  }

  function getCellMovingStatus() {
    var _cascaderRef$current;

    return cascade ? ((_cascaderRef$current = cascaderRef.current) == null ? void 0 : _cascaderRef$current.getCellMovingStatus()) || [] : pickerCellsRef.current.map(function (cell) {
      return cell.movingStatus;
    });
  }

  function scrollToCurrentIndex() {
    if (cascade) {
      var _cascaderRef$current2;

      (_cascaderRef$current2 = cascaderRef.current) == null ? void 0 : _cascaderRef$current2.scrollToCurrentIndex();
      return;
    }

    pickerCellsRef.current.forEach(function (cell) {
      return cell.scrollToCurrentIndex();
    });
  }

  useImperativeHandle(ref, function () {
    return {
      dom: domRef.current,
      getCellMovingStatus: getCellMovingStatus,
      getAllColumnValues: getAllColumnValues,
      getColumnValue: getColumnValue,
      updateLayout: updateLayout,
      resetValue: resetValue,
      scrollToCurrentIndex: scrollToCurrentIndex
    };
  });

  function _onPickerChange(val, index) {
    setScrollValue(val);

    if (onPickerChange) {
      onPickerChange(val, index);
    }
  }

  function _onValueChange(val, index) {
    setScrollValue(val);

    if (onPickerChange) {
      onPickerChange(val, index);
    }
  }

  useEffect(function () {
    setScrollValue(value);
  }, [value, setScrollValue]);
  var updateWrapperHeight = useCallback(function () {
    if (wrapperRef && wrapperRef.current) {
      setWrapperHeight(wrapperRef.current.offsetHeight);
    }
  }, [wrapperRef, setWrapperHeight]);

  var updateItemHeight = function updateItemHeight() {
    if (barRef.current) {
      setItemHeight(barRef.current.getBoundingClientRect().height);
    }
  };

  useEffect(function () {
    updateLayout();
  }, []);

  function updateLayout() {
    updateWrapperHeight();
    updateItemHeight();
  }

  function resetValue() {
    setScrollValue(value);
  }

  var newItemStyle = _extends({
    height: itemHeight + "px"
  }, itemStyle);

  return /*#__PURE__*/React.createElement(ContextLayout, null, function (_ref) {
    var prefixCls = _ref.prefixCls;
    return /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-picker-view all-border-box " + className,
      style: {
        height: itemHeight * rows + "px"
      },
      ref: wrapperRef
    }, cascade ? /*#__PURE__*/React.createElement(Cascader, {
      prefixCls: prefixCls || '',
      cols: cols,
      itemStyle: newItemStyle,
      data: data,
      selectedValue: scrollValue,
      onValueChange: _onValueChange,
      onPickerChange: _onPickerChange,
      clickable: clickable,
      itemHeight: itemHeight,
      wrapperHeight: wrapperHeight,
      disabled: disabled,
      rows: rows,
      hideEmptyCols: hideEmptyCols,
      ref: cascaderRef,
      touchToStop: touchToStop
    }) : /*#__PURE__*/React.createElement(MultiPicker, {
      prefixCls: prefixCls + "-picker",
      data: innerData,
      itemHeight: itemHeight,
      selectedValue: scrollValue,
      onValueChange: _onValueChange,
      onPickerChange: _onPickerChange
    }, innerData.map(function (item, index) {
      return /*#__PURE__*/React.createElement(PickerCell, {
        key: index + "_picker_cell_normal",
        data: item,
        style: newItemStyle,
        prefixCls: prefixCls + "-picker",
        clickable: clickable,
        itemHeight: itemHeight,
        wrapperHeight: wrapperHeight,
        disabled: disabled,
        rows: rows,
        hideEmptyCols: hideEmptyCols,
        ref: function ref(cellRef) {
          return cellRef && (pickerCellsRef.current[index] = cellRef);
        },
        touchToStop: touchToStop
      });
    })), /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-picker-selection"
    }, /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-picker-selection-mask " + prefixCls + "-picker-selection-mask-top"
    }), /*#__PURE__*/React.createElement("div", {
      ref: barRef,
      className: prefixCls + "-picker-selection-bar"
    }), /*#__PURE__*/React.createElement("div", {
      className: prefixCls + "-picker-selection-mask " + prefixCls + "-picker-selection-mask-bottom"
    })));
  });
});
export default PickerView;